<template>
  <DialogRoot
    :open="isOpen || isStorybook"
    :modal="!isStorybook"
    @update:open="closeModal"
  >
    <DialogPortal :disabled="isStorybook">
      <div class="fixed" :class="$style.root">
        <DialogOverlay
          class="fixed inset-0 -z-10 bg-gray-100 bg-opacity-50"
          :class="$style.overlay"
        />

        <DialogContent
          :aria-describedby="undefined"
          :class="[
            $style.content,
            `flex flex-col max-h-full overflow-hidden rounded-lg bg-white shadow-xl border border-gray-300`,
          ]"
        >
          <VisuallyHidden as-child>
            <DialogTitle>Quick search</DialogTitle>
          </VisuallyHidden>

          <ComboboxRoot
            v-model:search-term="query"
            :default-open="isStorybook"
            :filter-function="identity"
            class="contents"
            @update:model-value="act"
          >
            <div class="relative flex items-center">
              <div class="ml-3.5 grid w-6 place-items-center text-gray-600">
                <ArrowPathIcon
                  v-if="search.isFetching.value"
                  class="h-4 w-4 animate-spin"
                />
                <MagnifyingGlassIcon v-else class="h-4 w-4" />
              </div>

              <ComboboxInput
                class="h-12 grow border-0 bg-transparent px-3 text-sm placeholder:text-gray-400 focus:ring-0"
                placeholder="Search by email, name, content..."
              />
            </div>

            <ComboboxContent
              v-if="results.length > 0"
              class="min-h-0 border-t border-gray-200"
            >
              <ComboboxViewport
                class="flex flex-col gap-3 overflow-y-auto px-4 py-3"
              >
                <ComboboxGroup v-for="section in sections" :key="section.title">
                  <ComboboxLabel
                    class="mb-2 -ml-[2px] text-[0.8125rem] leading-5 font-semibold text-gray-500 empty:hidden"
                  >
                    {{ section.title }}
                  </ComboboxLabel>

                  <SearchItem
                    v-for="(item, index) in section.items"
                    :key="`${index}-${item.type}-${item.obj.id}`"
                    :item="item"
                  />
                </ComboboxGroup>

                <ComboboxItem
                  :key="query"
                  :value="createViewAllAction(query)"
                  class="-mx-2 flex scroll-m-3 gap-3 rounded-md px-1.5 py-1 text-gray-700 select-none data-[highlighted]:bg-gray-100 data-[highlighted]:text-gray-900"
                >
                  <div
                    class="flex shrink-0 items-center justify-center self-start rounded bg-gray-200 p-1"
                  >
                    <EllipsisIcon class="h-4 w-4" />
                  </div>
                  <span class="my-0.5 grow text-sm font-medium">
                    View all results &rarr;
                  </span>
                </ComboboxItem>
              </ComboboxViewport>
            </ComboboxContent>
            <ComboboxContent
              v-else-if="query && !search.isFetching.value"
              class="px-6 py-14 text-center text-sm"
            >
              <ExclamationTriangleIcon
                icon="exclamation-triangle"
                class="mx-auto size-6 text-gray-500"
              />
              <p class="mt-4 font-semibold text-gray-900">
                No results found for "{{ query }}"
              </p>
              <p class="mt-2 text-gray-500">
                No components found for this search term. Please try again.
              </p>
            </ComboboxContent>
          </ComboboxRoot>
        </DialogContent>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<script lang="ts">
export default {
  name: "SearchModal",
};
</script>

<script lang="ts" setup>
import {
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxRoot,
  ComboboxViewport,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from "radix-vue";
import { computed, ref } from "vue";
import { useRouter } from "vue-router/auto";

import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import EllipsisIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";
import ExclamationTriangleIcon from "@/icons/heroicons/exclamation-triangle-solid.svg";
import MagnifyingGlassIcon from "@/icons/heroicons/magnifying-glass-micro.svg";
import { useSearchQuery } from "@/lib/api/queries/search/list";
import { type Action, createViewAllAction } from "@/lib/search";
import { useStore as useScaffoldingStore } from "@/store/stores/scaffolding";

import SearchItem from "./SearchItem.vue";

const emit = defineEmits(["close", "update:modelValue"]);

const router = useRouter();
const scaffolding = useScaffoldingStore();

const isStorybook = computed(() => window.location.pathname.includes("memex"));

const identity = (value: any) => value;

const props = defineProps<{
  // Just used for storybook. Gross, I know.
  defaultQuery?: string;
}>();

const isOpen = ref(true);
const closeModal = () => {
  isOpen.value = false;
  setTimeout(() => emit("close"), 250);
};

const query = ref(props.defaultQuery || "");
const search = useSearchQuery({ q: query, brief: true });

const results = computed(() => search.data.value);

const act = (_action: unknown) => {
  const action = _action as Action;

  if (action.type === "route") {
    router.push(action.route);
  } else if (action.type === "action") {
    action.action?.();
  }

  closeModal();
};

const newFeatures = computed(() => {
  const newsletter = scaffolding.newsletter;

  return results.value.filter((item) => {
    if (item.type !== "page" || item.feature === undefined) {
      return false;
    }

    return !newsletter?.enabled_features.includes(item.feature);
  });
});

const pages = computed(() => {
  const newsletter = scaffolding.newsletter;

  return results.value.filter((item) => {
    if (item.type !== "page") {
      return false;
    }

    return (
      item.feature === undefined ||
      newsletter?.enabled_features.includes(item.feature)
    );
  });
});

const stuff = computed(() => {
  return results.value.filter((item) => item.type !== "page");
});

const sections = computed(() => {
  return [
    {
      title: "Results",
      items: stuff.value,
    },
    {
      title: "Pages",
      items: pages.value,
    },
    {
      title: "Explore new features",
      items: newFeatures.value,
    },
  ].filter((section) => section.items.length > 0);
});
</script>

<style module>
.root {
  display: grid;
  place-items: start center;
  position: fixed;
  inset: 0;
  z-index: 30;

  & {
    padding: 16px;
  }
  @media (min-width: 640px) {
    padding: 24px;
  }
  @media (min-width: 768px) {
    padding: 80px;
  }
}

.overlay {
  &[data-state="open"] {
    animation: overlay-fade-in 250ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: overlay-fade-out 250ms cubic-bezier(0.16, 1, 0.3, 1);
  }
}

.content {
  width: 100%;
  max-width: 600px;

  &[data-state="open"] {
    animation: content-scale-in 250ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  &[data-state="closed"] {
    animation: content-scale-out 250ms cubic-bezier(0.16, 1, 0.3, 1);
  }
}

@keyframes overlay-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes overlay-fade-out {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes content-scale-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes content-scale-out {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.96);
  }
}
</style>
